"use client";

import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { brands } from "@/data/homeData";
import { 
  Code, 
  Rocket, 
  Video, 
  Zap, 
  Cloud, 
  ArrowRight,
  ExternalLink
} from "lucide-react";
import Brands3DCanvas from "./ui/Brands3DCanvas";

const iconMap: Record<string, any> = {
  code: Code,
  rocket_launch: Rocket,
  movie_edit: Video,
  psychology: Zap,
  cloud_done: Cloud,
};

const BrandsSection: React.FC = () => {
  const { t } = useLanguage();

  return (
    <section className="py-32 bg-[#040d21] relative overflow-hidden section-visibility" id="thuong-hieu">
      {/* 3D Background */}
      <Brands3DCanvas />

      {/* Decorative Glows */}
      <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#0066ff]/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-[#bd00ff]/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-[#00f3ff] font-bold text-[10px] uppercase tracking-[0.2em] mb-6 shadow-[0_0_10px_rgba(0,243,255,0.2)]">
            <span className="w-2 h-2 rounded-full bg-[#00f3ff] animate-pulse shadow-[0_0_10px_#00f3ff]"></span>
            {t("home.brands.badge")}
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-white leading-[1.1] tracking-tighter mb-6">
            {t("home.brands.title")}
          </h2>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto font-medium leading-relaxed">
            {t("home.brands.description")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {brands.map((brand, index) => {
            const IconComponent = iconMap[brand.icon] || Code;
            
            return (
              <div
                key={brand.id}
                className={`group relative p-10 bg-[#0a192f]/40 backdrop-blur-xl border border-white/5 hover:border-[#00f3ff]/30 rounded-[3rem] transition-all duration-700 hover:-translate-y-3 hover:shadow-[0_0_50px_rgba(0,243,255,0.1)] overflow-hidden flex flex-col h-full optimize-gpu ${
                  index >= 3 ? "lg:col-span-1.5 lg:max-w-md lg:mx-auto" : ""
                }`}
              >
                {/* Brand Logo/Icon Placeholder */}
                <div className="mb-8 flex items-center justify-between">
                  <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center group-hover:bg-[#00f3ff] group-hover:text-[#040d21] text-[#00f3ff] transition-all duration-500 group-hover:rotate-[10deg] shadow-inner">
                    <IconComponent className="w-8 h-8" />
                  </div>
                  <span className="text-white/5 font-black text-5xl group-hover:text-[#00f3ff]/10 transition-colors duration-500 italic">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>

                <h3 className="text-2xl font-black mb-4 text-white tracking-tight group-hover:text-[#00f3ff] transition-colors duration-500">
                  {t(`home.brands.items.${brand.slug}`).name}
                </h3>

                <p className="text-slate-400 leading-relaxed font-medium mb-10 flex-grow group-hover:text-slate-200 transition-colors duration-500">
                  {t(`home.brands.items.${brand.slug}`).description}
                </p>

                <div className="mt-auto">
                  <a
                    href={brand.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-bold text-[#00f3ff]/70 group-hover:text-[#00f3ff] transition-all"
                  >
                    <span>{t("home.news.viewDetails") || "Xem chi tiết"}</span>
                    <ExternalLink className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </a>
                </div>

                {/* Cyber Accents */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#00f3ff]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-1 h-0 bg-[#00f3ff] group-hover:h-1/2 transition-all duration-700" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BrandsSection;
